import React from 'react';
import { Sparkles, Search, Zap, Mic, Camera, Shield, MessageSquare, MousePointer2, LayoutPanelLeft } from 'lucide-react';

export type PreviewType = 'desktop' | 'chat' | 'mode' | 'windows';

export interface TutorialStep {
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  bullets: string[];
  icon: React.ReactNode;  // Note: Will be replaced with JSX at runtime
  accent: string;
  accentText: string;
  preview: PreviewType | null;
  isSensorStep?: boolean;
}

interface BuildStepArgs {
  ui: (zh: string, en: string) => string;
}

/**
 * 步骤数据工厂 — 根据当前语言生成步骤数组
 * accent 色使用 celestial-* 主题变量（随 6 套主题自动切换）
 */
export function buildSteps({ ui }: BuildStepArgs): TutorialStep[] {
  return [
    {
      eyebrow: ui('00 / 准备工作', '00 / Preparation'),
      title: ui('开启小枫桌面感知', 'Enable Desktop Awareness'),
      description: ui(
        '小枫 使用本地摄像头和麦克风信号用于存在感、语音和生物特征功能。传感器数据保留在本地，可以在设置中管理权限。',
        '小枫 uses local camera & microphone for presence, voice, and biometric features. Sensor data stays on-device; manage permissions in Settings anytime.'
      ),
      action: ui('点击"继续"确认并进入教程', 'Click "Continue" to confirm and start the tutorial'),
      bullets: [
        ui('摄像头：存在感、人脸识别和手势感知', 'Camera: presence, face recognition, gesture awareness'),
        ui('麦克风：语音通话、唤醒词和声纹', 'Microphone: voice calls, wake word, voiceprint'),
        ui('所有传感器数据优先本地处理', 'All sensor data is processed locally first'),
      ],
      icon: <Shield size={34} className="text-celestial-saturn" />,
      accent: 'bg-celestial-saturn',
      accentText: 'text-celestial-saturn',
      preview: null,
      isSensorStep: true,
    },
    {
      eyebrow: ui('01 / 桌面入口', '01 / Desktop Entry'),
      title: ui('先从桌面开始', 'Start From The Desktop'),
      description: ui('小枫 的主要能力都放在桌面图标、Dock 和搜索里。打开一个入口后，窗口会像桌面应用一样停留在工作区。', '小枫 keeps the main abilities in desktop icons, the dock, and search. Open an entry and its window stays in the workspace like a real desktop app.'),
      action: ui('试着打开 Chat 或 Skill Center', 'Try opening Chat or Skill Center'),
      bullets: [
        ui('双击桌面图标打开功能', 'Double-click desktop icons to open features'),
        ui('底部 Dock 保留常用入口', 'The dock keeps common entries nearby'),
        ui('已打开窗口会自动出现在 Dock', 'Open windows appear in the dock automatically'),
      ],
      icon: <Sparkles size={34} className="text-celestial-saturn" />,
      accent: 'bg-celestial-saturn',
      accentText: 'text-celestial-saturn',
      preview: 'desktop',
    },
    {
      eyebrow: ui('02 / 交代任务', '02 / Give A Task'),
      title: ui('用一句话告诉 小枫 要做什么', 'Tell 小枫 What To Do In One Sentence'),
      description: ui('你可以点中心球、打开 Chat，或者用麦克风说话。适合让它打开软件、处理文件、查信息、执行连续步骤。', 'Click the center orb, open Chat, or speak through the microphone. Use this for opening apps, handling files, checking information, and running multi-step work.'),
      action: ui('输入或说出一个明确任务', 'Type or speak a clear task'),
      bullets: [
        ui('文字适合复杂任务', 'Text is best for complex tasks'),
        ui('语音适合快速指令', 'Voice is best for quick commands'),
        ui('执行前会显示关键状态', 'Key execution states stay visible'),
      ],
      icon: <MessageSquare size={34} className="text-celestial-glow" />,
      accent: 'bg-celestial-glow',
      accentText: 'text-celestial-glow',
      preview: 'chat',
    },
    {
      eyebrow: ui('03 / 执行模式', '03 / Execution Mode'),
      title: ui('先看模式，再让 AI 动手', 'Check The Mode Before 小枫 Acts'),
      description: ui('模式会影响 小枫 的执行方式。会议负责记录，聊天适合交流，助手会按任务调用能力，自主适合多步可见流程。', 'Modes affect how 小枫 acts. Meeting records notes, Chat is conversation, Assistant chooses abilities for the task, and Autonomy handles visible multi-step work.'),
      action: ui('根据任务切换聊天 / 助手 / 自主', 'Switch between Chat / Assistant / Autonomy'),
      bullets: [
        ui('聊天：只回答，不主动动手', 'Chat: answer only, no proactive actions'),
        ui('助手：按需调用工具、技能和团队', 'Assistant: use tools, skills, and teams as needed'),
        ui('自主：多步任务，敏感操作仍会确认', 'Autonomy: multi-step work, sensitive actions still require confirmation'),
      ],
      icon: <MousePointer2 size={34} className="text-celestial-nebula" />,
      accent: 'bg-celestial-nebula',
      accentText: 'text-celestial-nebula',
      preview: 'mode',
    },
    {
      eyebrow: ui('04 / 窗口管理', '04 / Window Management'),
      title: ui('像桌面一样管理窗口', 'Manage Windows Like A Desktop'),
      description: ui('Chat、Skill Center、Knowledge Base 等打开后都作为独立窗口停留在桌面。你可以拖拽、调整大小、最小化到 Dock，像真正的桌面应用一样管理。', 'Chat, Skill Center, Knowledge Base, and more open as independent windows on the desktop. Drag, resize, minimize to Dock just like real desktop apps.'),
      action: ui('打开一个功能窗口，试试拖拽标题栏移动', 'Open a feature window and try dragging its title bar'),
      bullets: [
        ui('拖拽窗口标题栏即可移动', 'Drag the title bar to move windows'),
        ui('最小化后窗口缩入底部 Dock', 'Minimized windows shrink into the Dock'),
        ui('所有窗口独立工作，互不干扰', 'All windows work independently'),
      ],
      icon: <LayoutPanelLeft size={34} className="text-celestial-glow" />,
      accent: 'bg-celestial-nebula',
      accentText: 'text-celestial-nebula',
      preview: 'windows',
    },
  ];
}
